import { useGetInline } from "@rentiohq/shared-frontend/dist/hooks/api.hooks";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { EAccountRole } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import apiUtils from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Loading,
  ResourceList,
  ResourceListItem,
} from "@rentiohq/web-shared/dist/components";
import { ChangeRoleModal } from "components/ChangeRoleModal";
import { IEnabledAccount } from "components/EnabledAccountsSelect/EnabledAccountsSelect";
import React from "react";

export const Employees = () => {
  const { broker } = authHooks.useSelf();
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);
  const isOfficeAdmin = authHooks.useIsOfficeAdmin();

  const [selectedEmployee, setSelectedEmployee] = React.useState<
    IEnabledAccount | undefined
  >(undefined);

  const { data, isFetching, fetchData } = useGetInline<IEnabledAccount[]>({
    path: broker ? `/brokers/${broker.id}/employee-accounts` : undefined,
  });

  const [connections, setConnections] = React.useState<
    Array<{ userId: number; status: string; email: string | null }>
  >([]);

  React.useEffect(() => {
    if (!hasCalendarSync) return;
    const fetchConnections = async () => {
      try {
        const res = await apiUtils.get<any>("/cronofy/connections", {
          isV2: true,
        });
        setConnections(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch cronofy connections", err); // eslint-disable-line no-console
      }
    };
    fetchConnections();
  }, [hasCalendarSync]);

  const getRoleLabel = (roles?: EAccountRole[]) =>
    roles?.includes(EAccountRole.OfficeAdmin)
      ? getLocalizedText("account_role.office_admin")
      : getLocalizedText("account_role.employee");

  // render
  const renderItem = (item: IEnabledAccount) => {
    const connection = connections.find(c => c.userId === item.userId);
    const isConnected = connection?.status === "CONNECTED";

    return (
      <ResourceListItem
        item={item}
        actions={
          isOfficeAdmin
            ? [
                {
                  content: getLocalizedText("account_role.action.change_role"),
                  onClick: () => setSelectedEmployee(item),
                },
              ]
            : []
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div>
            <div>{getName(item)}</div>
            <div>{getRoleLabel(item.accountRoles)}</div>
          </div>

          {hasCalendarSync &&
            (isConnected ? (
              <div
                style={{
                  color: "#00A87A",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {getLocalizedText("cronofy.connection.status.connected")}{" "}
                {connection?.email ? `(${connection.email})` : ""}
              </div>
            ) : (
              <div style={{ color: "#888", fontSize: "14px" }}>
                {getLocalizedText("cronofy.connection.status.disconnected")}
              </div>
            ))}
        </div>
      </ResourceListItem>
    );
  };

  if (isFetching && !data) {
    return <Loading />;
  }

  return (
    <>
      <ResourceList items={data || []} renderItem={renderItem} />

      {selectedEmployee && (
        <ChangeRoleModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(undefined)}
          onSuccess={() => {
            setSelectedEmployee(undefined);
            fetchData(false);
          }}
        />
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default Employees;
